import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { PopoverBody, Popover } from 'reactstrap'
import classNames from 'classnames'

import { ConfirmationModal } from '@elo-kit/components/elo-ui/confirmation-modal/ConfirmationModal'

import { createId } from '@elo-kit/utils/general.utils'

import { POSITIONS } from 'constants/general.constants'

import './community-tooltip-menu.scss'

export const SPACE_ORIENTATION = {
  horizontal: 'h',
  vertical: 'v',
}

const CommunityTooltipMenu = (props) => {
  const {
    children,
    container,
    id,
    iconMenu,
    iconOrientation,
    placement,
    popoverClass,
    menuItems,
    customIcon,
    alignRight,
    defaultModalTitleText,
    submitButtonChild,
  } = props

  const [popoverOpen, setPopoverOpen] = useState(false)
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false)
  const [confirmationAction, setConfirmationAction] = useState(null)
  const [modalBodyText, setModalBodyText] = useState('')
  const [modalTitleText, setModalTitleText] = useState('')

  const toggleConfirmationModal = (action = null) => {
    setConfirmationModalOpened(!confirmationModalOpened)
    setConfirmationAction(action)
  }

  const togglePopover = () => {
    setPopoverOpen((prevPopover) => !prevPopover)
  }

  const handleTooltipMenuClick = (item) => {
    item.action()
    if (!item.keepPopoverAfterAction) {
      togglePopover()
    }
  }

  const popoverClassNames = classNames(
    'community-tooltip-menu__popover',
    {
      'community-tooltip-menu__popover--with-icons': iconMenu,
    },
    popoverClass
  )

  const containerClassNames = classNames('community-tooltip-menu__container', {
    'community-tooltip-icon-menu': iconMenu,
    'community-tooltip-menu__container--right': alignRight,
  })

  const target = `menu-tooltip-with-icons-${id}`

  if (!children && menuItems.every((item) => !item.show)) {
    return null
  }

  return (
    <>
      <div
        className={!customIcon ? containerClassNames : ''}
        aria-expanded
        aria-haspopup
        id={target}
        onKeyPress={togglePopover}
        role='button'
      >
        {customIcon ? (
          customIcon({
            onClick: togglePopover,
            id: target,
          })
        ) : (
          <i className={`far fa-ellipsis-${iconOrientation} community-tooltip-menu__icon`} />
        )}
        <Popover
          className='community-tooltip-menu__popover-container'
          innerClassName={String(popoverClassNames)}
          isOpen={popoverOpen}
          toggle={togglePopover}
          {...{
            container,
            placement,
            target,
          }}
        >
          <PopoverBody>
            {typeof children === 'function' ? children(togglePopover) : children}
            {menuItems.map((item, index) => {
              const { action, bodyText, content, id: itemId, label, show, titleText, useConfirmation } = item

              const handleClick = () => {
                if (useConfirmation) {
                  toggleConfirmationModal(action)
                  togglePopover()
                  setModalBodyText(bodyText)
                  setModalTitleText(titleText)
                } else {
                  handleTooltipMenuClick(item)
                }
              }

              return (
                <Fragment key={`menu-tooltip-${id}-${itemId || index}`}>
                  {show && (
                    <button
                      aria-expanded
                      id={createId(id, index)}
                      className='community-tooltip-menu__item'
                      key={createId(id, index)}
                      onClick={handleClick}
                      onKeyPress={handleClick}
                    >
                      {iconMenu ? (
                        <i className={String(label)} id={`menu-tooltip-icon-${id}-${itemId}`} />
                      ) : (
                        !!label && label
                      )}
                      {content}
                    </button>
                  )}
                </Fragment>
              )
            })}
          </PopoverBody>
        </Popover>
      </div>
      <ConfirmationModal
        isOpen={confirmationModalOpened}
        submit={confirmationAction}
        close={toggleConfirmationModal}
        toggle={toggleConfirmationModal}
        title={modalTitleText || defaultModalTitleText}
        message={modalBodyText}
        submitButtonChild={submitButtonChild}
      />
      {popoverOpen && <div className='community-tooltip-menu-close' onClick={togglePopover} />}
    </>
  )
}

CommunityTooltipMenu.displayName = 'CommunityTooltipMenu'
CommunityTooltipMenu.propTypes = {
  container: PropTypes.node,
  placement: PropTypes.string,
  iconMenu: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  popoverClass: PropTypes.string,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      show: PropTypes.bool,
      action: PropTypes.func.isRequired,
      label: PropTypes.string,
      content: PropTypes.any,
      keepPopoverAfterAction: PropTypes.bool,
    })
  ),
  iconOrientation: PropTypes.oneOf(Object.values(SPACE_ORIENTATION)),
  children: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.node,
    PropTypes.element,
    PropTypes.func,
  ]),
  customIcon: PropTypes.func,
  alignRight: PropTypes.bool,
  defaultModalTitleText: PropTypes.string,
  submitButtonChild: PropTypes.string,
}
CommunityTooltipMenu.defaultProps = {
  placement: POSITIONS.left,
  iconMenu: false,
  iconOrientation: SPACE_ORIENTATION.horizontal,
  menuItems: [],
  defaultModalTitleText: I18n.t('react.common.are_you_sure'),
  submitButtonChild: I18n.t('react.cabinet.common.confirm'),
}

export default CommunityTooltipMenu
